import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, 
  faCheckCircle, 
  faTimesCircle,
  faBan,
  faMessage
} from '@fortawesome/free-solid-svg-icons';

const MyRequestsSection = ({ requests, onCancel, onRefresh }) => {
  const navigate = useNavigate();
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return { icon: faClock, color: '#fbbf24' };
      case 'accepted':
        return { icon: faCheckCircle, color: '#10b981' };
      case 'rejected':
        return { icon: faTimesCircle, color: '#ef4444' };
      case 'cancelled':
        return { icon: faBan, color: '#666' };
      default:
        return { icon: faClock, color: '#999' };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      border: '1px solid #333',
      borderRadius: '12px',
      padding: '1.5rem',
      marginBottom: '2rem'
    }}>
      <h2 style={{ 
        color: '#FFB6C1', 
        marginBottom: '1.5rem',
        fontSize: '1.5rem'
      }}>
        My Match Requests
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {requests.map(request => {
          const statusInfo = getStatusIcon(request.status);
          
          return (
            <div
              key={request.id}
              style={{
                backgroundColor: '#000',
                border: '1px solid #333',
                borderRadius: '8px',
                padding: '1.25rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '1rem'
              }}
            >
              {/* Left Side - Info */}
              <div style={{ flex: 1 }}>
                {/* Facilitator Name */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#FFB6C1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#000'
                  }}>
                    {request.facilitator?.user?.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3 style={{ 
                      color: '#FFB6C1', 
                      margin: 0,
                      marginBottom: '0.25rem',
                      fontSize: '1.1rem'
                    }}>
                      {request.facilitator?.user?.full_name || 'Unknown Facilitator'}
                    </h3>
                    <p style={{ 
                      color: '#999', 
                      margin: 0,
                      fontSize: '0.85rem'
                    }}>
                      Requested {formatDate(request.requested_at)}
                    </p>
                  </div>
                </div>

                {/* Status */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: `${statusInfo.color}20`,
                  border: `1px solid ${statusInfo.color}`,
                  borderRadius: '20px',
                  marginBottom: '0.75rem'
                }}>
                  <FontAwesomeIcon icon={statusInfo.icon} style={{ color: statusInfo.color }} />
                  <span style={{ 
                    color: statusInfo.color,
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    textTransform: 'capitalize'
                  }}>
                    {request.status}
                  </span>
                </div>

                {/* Request Message */}
                <p style={{ 
                  color: '#ccc', 
                  margin: '0.75rem 0',
                  fontSize: '0.95rem',
                  lineHeight: '1.5'
                }}>
                  "{request.request_message}"
                </p>

                {/* Concern Areas */}
                {request.concern_areas && request.concern_areas.length > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '0.5rem',
                    marginTop: '0.75rem'
                  }}>
                    {request.concern_areas.map((area, index) => (
                      <span
                        key={index}
                        style={{
                          padding: '0.25rem 0.625rem',
                          backgroundColor: '#1a1a1a',
                          border: '1px solid #555',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          color: '#999'
                        }}
                      >
                        {area.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                )}

                {/* Response Message (if accepted or rejected) */}
                {request.response_message && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    backgroundColor: '#1a1a1a',
                    border: `1px solid ${statusInfo.color}`,
                    borderRadius: '8px'
                  }}>
                    <p style={{ 
                      color: '#999',
                      fontSize: '0.85rem',
                      marginBottom: '0.375rem'
                    }}>
                      Facilitator's Response:
                    </p>
                    <p style={{ 
                      color: '#ccc',
                      fontSize: '0.95rem',
                      margin: 0
                    }}>
                      "{request.response_message}"
                    </p>
                    {request.responded_at && (
                      <p style={{ 
                        color: '#666',
                        fontSize: '0.8rem',
                        margin: '0.5rem 0 0 0'
                      }}>
                        {formatDate(request.responded_at)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Right Side - Actions */}
              {request.status === 'pending' && (
                <button
                  onClick={() => onCancel(request.id)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#ef4444',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Cancel Request
                </button>
              )}
              {request.status === 'accepted' && request.match_id && (
                <button
                  onClick={() => navigate(`/buddy/chat/${request.match_id}`)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#3b82f6',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FontAwesomeIcon icon={faMessage} /> Start Chat
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyRequestsSection;
